/**
 * WithdrawConsent - the page a PARENT lands on to change their mind
 * -------------------------------------------------------------------
 * The consent email tells them they can withdraw at any time. Until this
 * existed, that meant replying to an email and waiting for somebody to read
 * it - which is a request, not a right. This is the same decision made in one
 * place, by them, at the moment they decide to make it.
 *
 * Like the consent page, it is written for somebody who is not logged in, did
 * not choose to be here, and may be worried. So it says plainly what will
 * happen BEFORE the button, and what has happened after.
 *
 * TWO THINGS IT IS CAREFUL ABOUT
 *
 * 1. Withdrawing does not delete the account. A parent saying "not for now"
 *    is not the same as asking for everything to be erased, and deleting
 *    would take the student's reviews and history with it. The page says so,
 *    and says how to ask for deletion if that is what they actually want.
 *
 * 2. Nothing happens on load. Opening the link only reads; the change needs
 *    the button. Mail scanners follow links to check them, so a page that
 *    acted on arrival would revoke consent before the parent had opened the
 *    message.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShieldAlert, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

import { consentApi } from '../services/api';
import { dateOnly } from '../utils/format';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

export default function WithdrawConsent() {
  const { t } = useI18n();
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    consentApi
      .readWithdraw(token)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const withdraw = async () => {
    setBusy(true);
    setError('');
    try {
      await consentApi.withdraw(token);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader label={t('common.checkingLink')} />;

  // ---- Link no longer valid ----
  if (!data) {
    return (
      <div className="auth-layout">
        <div className="card">
          <div className="alert alert-error">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>{error || t('withdraw.invalid')}</div>
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
            {t('withdraw.replyToWithdraw')}
          </p>
        </div>
      </div>
    );
  }

  const student = data.student;

  // ---- Done ----
  if (done) {
    return (
      <div className="auth-layout">
        <div className="card">
          <div className="alert alert-success">
            <CheckCircle2 size={20} aria-hidden="true" />
            <div>
              <strong>{t('withdraw.doneTitle')}</strong>{' '}
              {t('withdraw.doneBody', { name: student.firstName })}
            </div>
          </div>

          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
            {t('withdraw.deleteAltogether')}
          </p>

          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
            {t('withdraw.canAskAgain', { name: student.firstName })}
          </p>

          <Link to="/" className="btn btn-outline btn-block" style={{ marginTop: 'var(--space-sm)' }}>
            {t('withdraw.aboutChoreConnect')}
          </Link>
        </div>
      </div>
    );
  }

  // ---- Already withdrawn, or consent lapsed on its own ----
  if (data.alreadyWithdrawn) {
    return (
      <div className="auth-layout">
        <div className="card">
          <div className="alert alert-info">
            <Info size={20} aria-hidden="true" />
            <div>
              <strong>{t('withdraw.nothingTitle')}</strong>{' '}
              {t('withdraw.nothingBody', { name: student.firstName })}
            </div>
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
            {t('withdraw.nothingWhy')}
          </p>
        </div>
      </div>
    );
  }

  // ---- The decision ----
  return (
    <div className="container section" style={{ maxWidth: 620 }}>
      <div className="page-header text-center">
        <span className="feature-icon amber" style={{ margin: '0 auto var(--space-sm)' }}>
          <ShieldAlert size={28} aria-hidden="true" />
        </span>
        <h1>{t('withdraw.title')}</h1>
        <p style={{ marginInline: 'auto' }}>
          {t('withdraw.forWhom', { name: `${student.firstName} ${student.lastName}` })}
          {data.consentedAt ? t('withdraw.agreedOn', { date: dateOnly(data.consentedAt) }) : ''}.
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('withdraw.whatHappens')}</h3>

        <div className="stack" style={{ gap: 10 }}>
          {[
            t('withdraw.effect1'),
            t('withdraw.effect2'),
            t('withdraw.effect3'),
            t('withdraw.effect4'),
          ].map((line) => (
            <span key={line} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <CheckCircle2 size={18} className="text-green" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden="true" />
              <span className="text-sm">{line}</span>
            </span>
          ))}
        </div>

        <hr className="divider" />

        <p className="text-sm text-muted">
          <strong>{t('withdraw.deleteInstead')}</strong>{t('withdraw.deleteInsteadBody')}
        </p>
      </div>

      <div className="card">
        <button
          type="button"
          className="btn btn-danger btn-block"
          onClick={withdraw}
          disabled={busy}
        >
          <ShieldAlert size={18} />
          {busy
            ? t('withdraw.saving')
            : t('withdraw.yesWithdraw', { name: student.firstName })}
        </button>

        <Link to="/" className="btn btn-ghost btn-block" style={{ marginTop: 'var(--space-xs)' }}>
          {t('withdraw.noLeave')}
        </Link>

        <p className="text-sm text-muted text-center" style={{ marginTop: 'var(--space-sm)' }}>
          You can change your mind again later. {student.firstName} can ask you from their account,
          and nothing happens unless you agree to it.
        </p>
      </div>
    </div>
  );
}
